import { CLAIM_GROUP_CONSULTANT_ROLE_NAMES } from '../constants';
export function getLeadConsultant(claimGroup) {
  const consultants = claimGroup.consultants || claimGroup.consultants_attributes || claimGroup.consultant_roles;
  const leadConsultant = consultants.find(consultant => consultant.role === 'claim_group_access_lead_consultant');
  return leadConsultant || null;
}
export function getSupportingConsultants(claimGroup) {
  const consultants = claimGroup.consultants || claimGroup.consultants_attributes || claimGroup.consultant_roles;
  const supportingConsultants = consultants.filter(consultant => consultant.role === 'claim_group_access_suporting_consultant');
  return supportingConsultants;
}
export function isUserClaimGroupConsultant(user, claimGroupOrChildObject) {
  // In case claim_group_id exists then its a child object of the claim group.
  // In case it doesnt and there is a role on the object's id - then it is the claim group.
  const roleResourceId = claimGroupOrChildObject.claim_group_id || claimGroupOrChildObject.id;
  const resourceRoles = user.roles?.filter(role => role.resource_id === roleResourceId);

  // the object wouldnt be a non claim group if it has claim group consultant role on itself
  const hasConsultantRole = resourceRoles.some(role => CLAIM_GROUP_CONSULTANT_ROLE_NAMES.includes(role.name));
  return hasConsultantRole;
}
export function isUserClaimGroupManager(user, claimGroup) {
  return user.id === claimGroup?.admin?.id;
}
export function isUserAdmin(user) {
  return user.account_type === 'admin' || user.account_type === 'manager' || user.account_type === 'super_consultant';
}
export function isUserCustomer(user) {
  return user.account_type === 'customer';
}
function warnIfUserDoesNotHaveUserAccessesArray(user) {
  if (!user.user_accesses) {
    console.warn('User without user_accesses passed to getClaimGroupAccesses. This wont work properly!');
  }
}
export function getClaimGroupAccesses(user, claimGroup) {
  warnIfUserDoesNotHaveUserAccessesArray(user);
  const claimGroupUserAccesses = user.user_accesses?.filter(user_access => {
    return user_access.rollable_type === 'ClaimGroup' && user_access.rollable_id === claimGroup.id;
  });
  return claimGroupUserAccesses;
}
export function getClaimGroupUserAccessRoles(user, claimGroup) {
  warnIfUserDoesNotHaveUserAccessesArray(user);
  const userClaimGroupAccesses = getClaimGroupAccesses(user, claimGroup);
  const userClaimGroupAccessRoles = userClaimGroupAccesses?.map(userAccess => userAccess.roles).flatMap(x => x);
  return userClaimGroupAccessRoles;
}
export function hasClaimGroupUserAccessWithRoles(user, claimGroup, rolesArray) {
  if (!Array.isArray(rolesArray)) {
    console.warn('Sent a non array argument to hasClaimGroupUserAccessWithRoles. This is not supported.');
    return false;
  }
  warnIfUserDoesNotHaveUserAccessesArray(user);
  const userClaimGroupAccessRoles = getClaimGroupUserAccessRoles(user, claimGroup);
  const anyRoleMatchesGiven = userClaimGroupAccessRoles?.some(role => rolesArray.includes(role));
  return !!anyRoleMatchesGiven;
}

// e.g. included_in_engagement_team, has_data_centre_access, etc
export function hasClaimGroupUserAccessWithSetting(user, claimGroup, setting) {
  if (typeof setting !== 'string') {
    console.warn('Sent a non string setting argument to hasClaimGroupUserAccessWithSetting. This is not supported.');
    return false;
  }
  if (!setting) {
    return false;
  }
  warnIfUserDoesNotHaveUserAccessesArray(user);

  // should be only one, but just in case
  const claimGroupAccesses = getClaimGroupAccesses(user, claimGroup);
  const result = claimGroupAccesses?.some(access => access[setting]);

  // force boolean output
  return !!result;
}

/**
 * Returns user_accesses from the claim group that are not already in any participation.
 * Used to build the list of selectable users (e.g. for adding a contact to a report).
 *
 * @param {Object} claimGroup - claim group with user_accesses
 * @param {Array} participations - list of participations, each with user_access
 * @returns {Array} user_access objects that can be selected (not already in participations)
 */
export function getSelectableUserAccesses(claimGroup, participations) {
  const userAccesses = claimGroup?.user_accesses ?? [];
  return userAccesses.filter(userAccess => participations?.every(p => p.user_access?.id !== userAccess.id));
}

/**
 * Returns dropdown options { key, value, text } for selectable user accesses
 * (claim group user_accesses not already in participations).
 *
 * @param {Object} claimGroup - claim group with user_accesses
 * @param {Array} participations - list of participations, each with user_access
 * @returns {Array} options for a dropdown: [{ key, value, text }, ...]
 */
export function getSelectableUserAccessDropdownOptions(claimGroup, participations) {
  return getSelectableUserAccesses(claimGroup, participations).map(userAccess => ({
    key: userAccess.id,
    value: userAccess.id,
    text: userAccess.user?.name ?? ''
  }));
}